import AnimationController from '../../../assets/js/controllers/animation';
import ResizeController from '../../../assets/js/controllers/resize';

import { getRootOffsetBounds } from '../../../assets/js/helpers';

import Platform from '../../../assets/js/constants/platform';
import VideoEvent from '../../../assets/js/constants/video-event';

export default class VideoPlayer {

	static selector = 'video-player';

	// class variables

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		container: null,
		progressBar: null,
	};

	el = {};

	flags = {
		isLoaded: false,
		isMouseOver: false,
		isMuted: false,
		isPlaying: false,
		isToggleVisible: false,
	};

	promises = {
		videoPlay: null
	};

	states = {
		duration: 0,
		platform: '',
		progress: 0
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			controls: {
				toggle: container.querySelector(`.${this.constructor.selector}__control-toggle`),
				mute: container.querySelector(`.${this.constructor.selector}__control-progress-mute`),
				progressWrapper: container.querySelector(`.${this.constructor.selector}__control-progress`),
				progressBar: container.querySelector(`.${this.constructor.selector}__control-progress-bar`),
				progressBarProgress: container.querySelector(`.${this.constructor.selector}__control-progress-bar-progress`)
			},
			video: container.querySelector('video')
		};

		this.initialize();
	}

	// initialize

	initialize() {
		const isStatic = this.el.container.classList.contains('is-static');

		if (!isStatic) {

			const willMute = this.el.video.hasAttribute('muted');
			const willPlay = this.el.container.hasAttribute('data-video-player-autoplay');

			if (willPlay) {
				this.videoPlay();
			}

			if (willMute) {
				this.videoMute();
			}

			this.#addEventListeners();
		}
	}

	// event listeners

	#addEventListeners = () => {
		this.el.container.addEventListener('click', this.#onClick);
		this.el.container.addEventListener('mouseenter', this.#onMouseEnter);
		this.el.container.addEventListener('mouseleave', this.#onMouseLeave);
		this.el.controls.mute.addEventListener('click', this.#onMuteClick);
		this.el.controls.progressBar.addEventListener('click', this.#onProgressBarClick);

		this.el.container.addEventListener(VideoEvent.TRIGGER_RELOAD, this.#onVideoPlayerTriggerReload);

		this.el.video.addEventListener('loadedmetadata', this.#onVideoLoadedMetaData);
		this.el.video.addEventListener('loadstart', this.#onVideoLoadStart);
		this.el.video.addEventListener('pause', this.#onVideoPause);
		this.el.video.addEventListener('play', this.#onVideoPlay);
		this.el.video.addEventListener('playing', this.#onVideoPlaying);

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});

		this.measure();
	}

	#removeEventListeners = () => {
		this.el.video.removeEventListener('loadedmetadata', this.#onVideoLoadedMetaData);
		this.el.video.removeEventListener('loadstart', this.#onVideoLoadStart);
		this.el.video.removeEventListener('pause', this.#onVideoPause);
		this.el.video.removeEventListener('play', this.#onVideoPlay);
		this.el.video.removeEventListener('playing', this.#onVideoPlaying);

		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);

		this.controllers.animation = null;
		this.controllers.resize = null;
	}

	// listener methods

	#onClick = (e) => {
		if (!this.el.controls.progressWrapper.contains(e.target)) {
			this.toggleVideoPlay();
		}
	}

	#onMouseEnter = () => {
		this.flags.isMouseOver = true;

	}

	#onMouseLeave = () => {
		this.flags.isMouseOver = false;
	}

	#onMuteClick = () => {
		this.toggleVideoMute();
	}

	#onProgressBarClick = (e) => {
		const ratio = (e.pageX - this.coords.progressBar.left) / this.coords.progressBar.width;
		const seconds = this.states.duration * ratio;
		this.videoProgressSeek(seconds);
	}

	#onVideoPlayerTriggerReload = (e) => {
		const willPlay = typeof e.detail.isPlaying === 'undefined' ? true : e.detail.isPlaying;
		this.videoReload(willPlay);
	}

	#onVideoLoadedMetaData = () => {
		this.states.duration = this.el.video.duration;
	}

	#onVideoLoadStart = () => {

	}

	#onVideoPause = () => {
		this.videoPauseRender();
	}

	#onVideoPlay = () => {
		this.videoPlayRender();
	}

	#onVideoPlaying = () => {
	}

	#onVideoPromiseComplete = () => {
		this.promises.videoPlay = null;
		if (!this.flags.isPlaying) {
			this.videoPause();
		}
	}

	// controller methods

	#onAnimationUpdate = () => {
		let willRender = false;
		if (this.states.duration) {
			const progressLast = this.states.progress;
			const progressNow = this.el.video.currentTime;
			if (progressLast !== progressNow) {
				this.states.progress = progressNow;
				willRender = true;
			}
		}

		return willRender;
	}

	#onAnimationRender = () => {
		const progressRatio = this.states.progress / this.states.duration;
		this.videoProgressRender(progressRatio);
	}

	#onResizeUpdate = () => this.measureResize();

	#onResizeRender = () => {
		if (this.states.platform !== Platform.MOBILE) {
			if (this.el.controls && this.el.controls.toggle) {
				this.el.controls.toggle.style.transform = '';
			}
		}

		if (this.flags.isPlaying) {
			this.videoReload();
		}
	}

	//

	measure() {
		this.measureResize();
	}

	measureResize() {
		this.states.platform = ResizeController.platform;
		this.coords.container = getRootOffsetBounds(this.el.container);
		this.coords.progressBar = getRootOffsetBounds(this.el.controls.progressBar);

		return ResizeController.isPlatformChange();
	}

	// video play status

	toggleVideoPlay() {
		if (this.flags.isPlaying) {
			this.videoPause();
		} else {
			this.videoPlay();
		}
	}

	videoPause() {
		this.videoPauseRender();

		if (!this.promises.videoPlay) {
			this.el.video.pause();
		}

		if (this.controllers.animation) {
			AnimationController.clear(this.controllers.animation);
			this.controllers.animation = null;
		}

		this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PAUSE, { detail: this.states }));
	}

	videoPauseRender() {
		this.flags.isPlaying = false;
		this.el.container.classList.remove('is-playing');
	}

	videoPlay() {
		if (!this.flags.isLoaded) {
			this.videoLoad();
		} else if (!this.flags.isPlaying) {
			this.videoPlayRender();

			try {
				this.promises.videoPlay = this.el.video.play();
				if (this.promises.videoPlay) {
					this.promises.videoPlay.then(this.#onVideoPromiseComplete);
				}

				if (!this.controllers.animation) {
					this.controllers.animation = AnimationController.set({
						update: this.#onAnimationUpdate,
						render: this.#onAnimationRender
					});
				}
				this.el.container.dispatchEvent(new CustomEvent(VideoEvent.PLAY, { detail: this.states }));
			} catch (error) {
				console.warn(error); // eslint-disable-line no-console
			}
		}
	}

	videoPlayRender() {
		this.flags.isPlaying = true;
		this.el.container.classList.add('is-playing');
	}

	videoLoad(willPlay = true) {
		if (!this.flags.isLoaded) {
			let $sources = [];
			if (this.states.platform !== Platform.MOBILE) {
				$sources = this.el.video.querySelectorAll('source[data-src-mobile]');
			} else if (this.states.platform !== Platform.TABLET) {
				$sources = this.el.video.querySelectorAll('source[data-src-tablet]');
			} else {
				$sources = this.el.video.querySelectorAll('source[data-src-desktop]');
			}

			let isValidSrc = false;
			for (let i = 0; i < $sources.length; i++) {
				const $source = $sources[i];
				const src = $source.getAttribute('data-src');
				if (src) {
					$source.setAttribute('src', src);
					isValidSrc = true;
				}
			}

			this.el.container.dispatchEvent(new CustomEvent(VideoEvent.LOAD, { detail: this.states }));

			if (isValidSrc) {
				this.el.container.classList.add('is-video-active');

				try {
					this.flags.isLoaded = true;
					this.el.video.load();
					if (willPlay) {
						this.videoPlay();
					}
				} catch (error) {
					this.flags.isLoaded = false;
					console.warn(error); // eslint-disable-line no-console
				}
			}

		} else {
			this.videoPlay();
		}
	}

	videoUnload() {
		if (this.flags.isLoaded) {
			this.flags.isLoaded = false;
			this.flags.isPlaying = false;
			const $sources = this.el.video.children;
			for (let i = 0; i < $sources.length; i++) {
				const $source = $sources[i];
				$source.removeAttribute('src');
			}
			this.el.container.classList.remove('is-video-active');
		}
	}

	videoReload(willPlay = true) {
		this.videoUnload();
		this.videoLoad(willPlay);
	}

	videoProgressSeek(seconds = 0) {
		this.el.video.currentTime = seconds;
		this.states.progress = seconds;
		this.videoProgressRender(this.states.progress / this.states.duration);
		this.el.container.dispatchEvent(new CustomEvent('VideoPlayerSeek', { detail: this.states }));
	}

	videoProgressRender(ratio) {
		this.el.controls.progressBarProgress.style.transform = `scaleX(${ratio})`;
		this.el.container.dispatchEvent(new CustomEvent('VideoPlayerProgress', { detail: this.states }));
	}

	// video sound

	toggleVideoMute() {
		if (!this.flags.isMuted) {
			this.videoMute();
		} else {
			this.videoUnmute();
		}
	}

	videoMute() {
		this.flags.isMuted = true;
		this.el.video.volume = 0;
		this.el.container.classList.add('is-muted');
	}

	videoUnmute() {
		this.flags.isMuted = false;
		this.el.video.volume = 1;
		this.el.container.classList.remove('is-muted');
	}

	//

	destroy() {
		this.#removeEventListeners();
	}

}
